import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { useToast } from '../../context/ToastContext';

export const SoftwareResourceModal = ({ isOpen, onClose, resourceToEdit }) => {
    const { toast } = useToast();
    const { resourceTypes, addSoftwareResource, updateSoftwareResource } = usePIM();

    const [formData, setFormData] = useState({
        name: '',
        type: '',
        version: '',
        release_date: '',
        description: '',
        web_link: '',
        visibility: 'public'
    });
    const [file, setFile] = useState(null);
    const [mode, setMode] = useState('upload'); // 'upload' or 'link'
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Filter resource types for Software (include NULL category as fallback)
    const softwareTypes = resourceTypes.filter(t => t.category === 'Software' || !t.category);

    useEffect(() => {
        if (resourceToEdit) {
            setFormData({
                name: resourceToEdit.name,
                type: resourceToEdit.type,
                version: resourceToEdit.version,
                release_date: resourceToEdit.release_date ? resourceToEdit.release_date.split('T')[0] : '',
                description: resourceToEdit.description || '',
                web_link: resourceToEdit.mime_type === 'link' ? resourceToEdit.file_path : '',
                visibility: resourceToEdit.visibility || 'public'
            });
            setMode(resourceToEdit.mime_type === 'link' ? 'link' : 'upload');
        } else {
            setFormData({
                name: '',
                type: softwareTypes.length > 0 ? softwareTypes[0].name : '',
                version: '1.0',
                release_date: new Date().toISOString().split('T')[0],
                description: '',
                web_link: '',
                visibility: softwareTypes.length > 0 ? (softwareTypes[0].default_visibility || 'public') : 'public'
            });
            setMode('upload');
            setFile(null);
        }
    }, [resourceToEdit, isOpen, resourceTypes]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            // Auto-fill name if empty
            if (!formData.name) {
                const fileNameWithoutExt = selectedFile.name.split('.').slice(0, -1).join('.');
                setFormData(prev => ({ ...prev, name: fileNameWithoutExt }));
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            // Auto-fill name if empty
            if (!formData.name) {
                const fileNameWithoutExt = droppedFile.name.split('.').slice(0, -1).join('.');
                setFormData(prev => ({ ...prev, name: fileNameWithoutExt }));
            }
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.type) {
            toast.error("Please fill in all required fields.");
            return;
        }

        if (resourceToEdit) {
            // Update
            await updateSoftwareResource(resourceToEdit.id, formData);
        } else {
            // Add
            const data = new FormData();
            data.append('name', formData.name);
            data.append('type', formData.type);
            data.append('version', formData.version);
            data.append('release_date', formData.release_date);
            data.append('description', formData.description);
            data.append('visibility', formData.visibility);

            if (mode === 'upload' && file) {
                data.append('file', file);
            } else if (mode === 'link') {
                data.append('web_link', formData.web_link);
            } else if (mode === 'upload' && !file) {
                toast.error("Please select a file.");
                return;
            }

            if (mode === 'upload' && file) {
                setIsUploading(true);
                setIsProcessing(false);
                setUploadProgress(0);
                try {
                    await addSoftwareResource(data, (progress) => {
                        setUploadProgress(progress);
                        if (progress >= 100) {
                            setIsProcessing(true);
                        }
                    });
                } catch (error) {
                    console.error("Upload failed", error);
                    toast.error("Upload failed");
                } finally {
                    setIsUploading(false);
                    setIsProcessing(false);
                }
            } else {
                await addSoftwareResource(data);
            }
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={resourceToEdit ? "Edit Resource" : "Add Resource"} maxWidth="max-w-2xl">
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Name</label>
                        <Input name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Firmware v1.0" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Type</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-blue-500"
                        >
                            {softwareTypes.map(t => (
                                <option key={t.id} value={t.name}>{t.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Version</label>
                        <Input name="version" value={formData.version} onChange={handleChange} placeholder="e.g. 1.0.0" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Release Date</label>
                        <Input type="date" name="release_date" value={formData.release_date} onChange={handleChange} />
                    </div>
                </div>

                <div className="grid grid-cols-1">
                    <div>
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Visibility</label>
                        <select
                            name="visibility"
                            value={formData.visibility}
                            onChange={handleChange}
                            className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-blue-500"
                        >
                            <option value="public">Public (Visible on Website)</option>
                            <option value="sensitive">Sensitive (Internal Only)</option>
                            <option value="blocked">Blocked (Hidden from Website)</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Description</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full bg-stone-950 border border-stone-800 rounded-lg px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-blue-500 h-24 resize-none"
                        placeholder="Optional description..."
                    />
                </div>

                {!resourceToEdit && (
                    <div className="bg-stone-900/50 p-4 rounded-lg border border-stone-800">
                        <div className="flex gap-4 mb-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="mode"
                                    checked={mode === 'upload'}
                                    onChange={() => setMode('upload')}
                                    className="accent-blue-500"
                                />
                                <span className="text-sm text-stone-300">Upload File</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="mode"
                                    checked={mode === 'link'}
                                    onChange={() => setMode('link')}
                                    className="accent-blue-500"
                                />
                                <span className="text-sm text-stone-300">Web Link</span>
                            </label>
                        </div>

                        {mode === 'upload' ? (
                            <div>
                                <div
                                    className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer transition-colors relative overflow-hidden ${isDragging ? 'border-blue-500 bg-blue-500/10' : (file ? 'border-blue-500 bg-blue-500/10' : 'border-stone-700 hover:border-stone-500 hover:bg-stone-800/50')}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => !isUploading && document.getElementById('sw-file-input').click()}
                                >
                                    {/* Progress Bar Background */}
                                    {isUploading && (
                                        <div
                                            className="absolute left-0 top-0 bottom-0 bg-blue-900/20 transition-all duration-300 ease-out"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    )}

                                    <input
                                        id="sw-file-input"
                                        type="file"
                                        className="hidden"
                                        onChange={handleFileChange}
                                        disabled={isUploading}
                                    />
                                    <div className="z-10 flex flex-col items-center">
                                        <p className="text-sm text-stone-300 font-medium mb-1">
                                            {isProcessing ? "Processing..." : (isUploading ? `Uploading... ${Math.round(uploadProgress)}%` : (file ? file.name : "Drag & Drop file here"))}
                                        </p>
                                        {!isUploading && !isProcessing && <p className="text-xs text-stone-500">or click to browse</p>}
                                    </div>

                                </div>
                            </div>
                        ) : (
                            <div>
                                <Input
                                    name="web_link"
                                    value={formData.web_link}
                                    onChange={handleChange}
                                    placeholder="https://example.com/resource"
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t border-stone-700">
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                    <PrimaryButton onClick={handleSave} disabled={isProcessing}>{resourceToEdit ? "Save Changes" : "Add Resource"}</PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};
